import { emit, listen } from "@tauri-apps/api/event";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import { getWindowDisplayId } from "./use-window-role";
import { getWindowOrigin } from "./use-window-origin";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";

const EVENT = "denker://floating-frame-display-area";
const REQUEST = "denker://floating-frame-display-area-request";
const areas = new Map<number, FocusWindowBox>();

export function rememberFloatingFrameDisplay(
  displayId: number,
  area: FocusWindowBox,
) {
  if (
    Object.values(area).every(Number.isFinite) &&
    area.width > 0 &&
    area.height > 0
  )
    areas.set(displayId, area);
}

/** Displays this window currently knows about (has exchanged geometry
 *  with) — used to tell a stale `expandedDisplayId` (its display unplugged,
 *  or never published here) from a real one. Pure read of module state, no
 *  Tauri calls, safe under jsdom. */
export function getKnownFloatingFrameDisplayIds(): number[] {
  return [...areas.keys()];
}

export function crossesFloatingFrameDisplay(
  box: FocusWindowBox,
  sourceId: number,
): boolean {
  return [...areas].some(
    ([id, area]) =>
      id !== sourceId &&
      box.x < area.x + area.width &&
      box.x + box.width > area.x &&
      box.y < area.y + area.height &&
      box.y + box.height > area.y,
  );
}

/** Each overlay supplies its own authoritative logical viewport, avoiding
 * physical-coordinate conversion on mixed-DPI desktops. */
export function attachFloatingFrameDisplays(): () => void {
  let disposed = false;
  let signature = "";
  const unlisteners: Array<() => void> = [];
  const publish = () => {
    if (document.documentElement.hasAttribute("data-first-run-native-window")) return;
    const origin = getWindowOrigin();
    const displayId = getWindowDisplayId();
    if (!origin.ready || displayId === null) return;
    const area = {
      x: origin.x,
      y: origin.y,
      width: window.innerWidth,
      height: window.innerHeight,
    };
    rememberFloatingFrameDisplay(displayId, area);
    const next = JSON.stringify({ displayId, area });
    if (next === signature) return;
    signature = next;
    void emit(EVENT, { displayId, area }).catch(() => {});
  };
  const add = (unlisten: () => void) => {
    if (disposed) safeTauriUnlisten(unlisten);
    else unlisteners.push(unlisten);
  };
  void Promise.all([
    listen<{ displayId: number; area: FocusWindowBox }>(
      EVENT,
      ({ payload }) => {
        rememberFloatingFrameDisplay(payload.displayId, payload.area);
      },
    ).then(add),
    listen(REQUEST, () => {
      signature = "";
      publish();
    }).then(add),
    listen<{ displayIds?: number[] }>("cursor-position", ({ payload }) => {
      if (payload.displayIds?.length) {
        for (const id of areas.keys())
          if (!payload.displayIds.includes(id)) areas.delete(id);
      }
      publish();
    }).then(add),
  ])
    .then(() => {
      if (!disposed) {
        publish();
        void emit(REQUEST).catch(() => {});
      }
    })
    .catch(() => {});
  return () => {
    disposed = true;
    unlisteners.forEach(safeTauriUnlisten);
    areas.clear();
  };
}

export function getFloatingFrameDisplayArea(
  displayId: number,
): FocusWindowBox | undefined {
  const area = areas.get(displayId);
  return area ? { ...area } : undefined;
}

/** Work areas exclude menu bars/docks, so choose the nearest rectangle. */
export function floatingFrameDisplayForPoint(
  point: { x: number; y: number },
  preferredDisplayId: number,
) {
  let nearest: { displayId: number; workArea: FocusWindowBox } | undefined;
  let nearestDistance = Infinity;
  for (const [displayId, area] of areas) {
    const dx = Math.max(area.x - point.x, 0, point.x - area.x - area.width);
    const dy = Math.max(area.y - point.y, 0, point.y - area.y - area.height);
    const distance = dx * dx + dy * dy;
    if (
      distance < nearestDistance ||
      (distance === nearestDistance && displayId === preferredDisplayId)
    ) {
      nearest = { displayId, workArea: { ...area } };
      nearestDistance = distance;
    }
  }
  return nearest;
}
